function defaultExtractErrorCodeFromText(text) {
    return String(text || "").match(/\bE\d{2,4}\b/i)?.[0]?.toUpperCase() || "";
}

function pickInstructionName(text, fallback) {
    const value = String(text || "").trim();
    const quoted = value.match(/["']([^"']{1,24})["']/);
    if (quoted?.[1]) return quoted[1].trim();
    const candidate = value
        .split(/[,\.\n\r\t]/)
        .map((part) => part.trim())
        .find((part) => part && !/(flow|create|generate|start|end)/i.test(part));
    return candidate ? candidate.slice(0, 24) : fallback;
}

export function removeDraftNodeById(draft, nodeId) {
    draft.nodes = (draft.nodes || []).filter((node) => node.id !== nodeId);
    draft.connections = (draft.connections || []).filter((connection) => connection.from !== nodeId && connection.to !== nodeId);
}

export function upsertDraftNode(draft, nextNode) {
    const nodes = Array.isArray(draft.nodes) ? draft.nodes : [];
    const index = nodes.findIndex((node) => node.id === nextNode.id);
    if (index >= 0) nodes[index] = { ...nodes[index], ...nextNode };
    else nodes.push(nextNode);
    draft.nodes = nodes;
}

export function removeDraftConnections(draft, predicate) {
    draft.connections = (draft.connections || []).filter((connection) => !predicate(connection));
}

export function upsertDraftConnection(draft, nextConnection) {
    const connections = Array.isArray(draft.connections) ? draft.connections : [];
    const index = connections.findIndex((connection) =>
        connection.from === nextConnection.from &&
        connection.to === nextConnection.to &&
        (connection.output || "output_1") === (nextConnection.output || "output_1") &&
        (connection.input || "input_1") === (nextConnection.input || "input_1")
    );
    if (index >= 0) connections[index] = { ...connections[index], ...nextConnection };
    else connections.push(nextConnection);
    draft.connections = connections;
}

export function findDraftAxis(draft, axisIndex = 0) {
    return Array.isArray(draft.axes) ? draft.axes[axisIndex] || null : null;
}

export function findDraftMotionById(draft, motionId) {
    for (const axis of draft.axes || []) {
        const motion = (axis.motions || []).find((row) => row.id === motionId);
        if (motion) return { axis, motion };
    }
    return null;
}

export function hasDraftConnection(draft, predicate) {
    return (draft?.connections || []).some(predicate);
}

export function findDraftConditionNode(draft) {
    return (draft?.nodes || []).find((node) => node.type === "condition") || null;
}

export function buildLocalFlowDraft(userMessage = "") {
    const text = String(userMessage || "");
    const hasCondition = /(condition|detect|on|off|timeout|sensor)/i.test(text);
    const hasError = /(error|alarm|timeout|E\d+)/i.test(text);
    const errorCode = defaultExtractErrorCodeFromText(text) || "E001";
    const conditionText = hasCondition ? pickInstructionName(text, "Work detect") : "Start check";
    const firstMotion = /(load|insert|infeed)/i.test(text) ? "Work load" : pickInstructionName(text, "Motion 1");
    const secondMotion = /(push|eject|outfeed)/i.test(text) ? "Work push" : "Motion 2";
    const nodes = [{ id: "start", type: "start", label: "Start", x: 40, y: 110 }];
    const connections = [];
    const axes = [{
        name: "X axis",
        motions: [
            { id: "m1", name: firstMotion, distance: 100, accel: 1, decel: 1, maxSpeed: 500, waitTime: 0 },
            { id: "m2", name: secondMotion, distance: 100, accel: 1, decel: 1, maxSpeed: 500, waitTime: 0 }
        ]
    }];

    if (hasCondition) {
        nodes.push({
            id: "cond1",
            type: "condition",
            conditionText,
            trueLabel: "ON",
            falseLabel: "OFF",
            timeoutLabel: "TIMEOUT",
            timeoutSec: 5,
            x: 250,
            y: 85
        });
        nodes.push({ id: "m1", type: "motion", axisName: "X axis", motionId: "m1", motionName: firstMotion, x: 520, y: 30 });
        nodes.push({ id: "m2", type: "motion", axisName: "X axis", motionId: "m2", motionName: secondMotion, x: 800, y: 30 });
        connections.push(
            { from: "start", to: "cond1", output: "output_1" },
            { from: "cond1", to: "m1", output: "output_1" },
            { from: "m1", to: "m2", output: "output_1" }
        );
        if (hasError) {
            nodes.push({ id: "err1", type: "error", errorCode, errorMessage: `${conditionText} timeout`, x: 520, y: 360 });
            connections.push({ from: "cond1", to: "err1", output: "output_3" });
        }
    } else {
        nodes.push({ id: "m1", type: "motion", axisName: "X axis", motionId: "m1", motionName: firstMotion, x: 260, y: 90 });
        nodes.push({ id: "m2", type: "motion", axisName: "X axis", motionId: "m2", motionName: secondMotion, x: 520, y: 90 });
        connections.push(
            { from: "start", to: "m1", output: "output_1" },
            { from: "m1", to: "m2", output: "output_1" }
        );
    }

    nodes.push({ id: "end", type: "end", label: "End", result: "normal", x: 1080, y: 90 });
    connections.push({ from: "m2", to: "end", output: "output_1" });
    return {
        flowDraftVersion: "1.0",
        summary: "Local draft created.",
        axes,
        nodes,
        connections
    };
}

export function ensureTimeoutErrorBranch(draft, errorCode) {
    const condition = findDraftConditionNode(draft);
    if (!condition) return;
    upsertDraftNode(draft, {
        id: "err_timeout",
        type: "error",
        errorCode: errorCode || "E001",
        errorMessage: `${condition.conditionText || "Condition"} timeout`,
        x: 520,
        y: 360
    });
    upsertDraftConnection(draft, { from: condition.id, to: "err_timeout", output: "output_3", input: "input_1" });
}

export function applyOffBranchToDraft(draft, branchValue) {
    const condition = findDraftConditionNode(draft);
    if (!condition) return;
    removeDraftNodeById(draft, "off_wait");
    removeDraftNodeById(draft, "off_wait_end");
    removeDraftNodeById(draft, "off_end");
    removeDraftNodeById(draft, "off_error");
    removeDraftConnections(draft, (connection) => connection.from === condition.id && (connection.output || "output_1") === "output_2");

    if (branchValue === "wait") {
        upsertDraftNode(draft, {
            id: "off_wait",
            type: "signal_output",
            label: "Wait",
            signalName: "WaitLamp",
            action: "ON",
            x: 520,
            y: 190
        });
        upsertDraftNode(draft, {
            id: "off_wait_end",
            type: "end",
            label: "Wait end",
            result: "normal",
            x: 800,
            y: 190
        });
        upsertDraftConnection(draft, { from: condition.id, to: "off_wait", output: "output_2", input: "input_1" });
        upsertDraftConnection(draft, { from: "off_wait", to: "off_wait_end", output: "output_1", input: "input_1" });
        return;
    }

    if (branchValue === "error") {
        upsertDraftNode(draft, {
            id: "off_error",
            type: "error",
            errorCode: "E002",
            errorMessage: "No detect error",
            x: 520,
            y: 190
        });
        upsertDraftConnection(draft, { from: condition.id, to: "off_error", output: "output_2", input: "input_1" });
        return;
    }

    upsertDraftNode(draft, {
        id: "off_end",
        type: "end",
        label: "No detect end",
        result: "abort",
        x: 520,
        y: 190
    });
    upsertDraftConnection(draft, { from: condition.id, to: "off_end", output: "output_2", input: "input_1" });
}

export function applyAfterMotionPlanToDraft(draft, nextStep) {
    const axis = findDraftAxis(draft);
    if (!axis || !(axis.motions || []).length) return;
    const motions = axis.motions;
    const secondMotion = motions[1] || motions[motions.length - 1];
    const endNode = (draft.nodes || []).find((node) => node.type === "end" && node.id === "end");

    removeDraftNodeById(draft, "m3");
    removeDraftConnections(draft, (connection) => connection.from === secondMotion.id || connection.to === "m3");

    if (nextStep === "next_motion") {
        if (!findDraftMotionById(draft, "m3")) {
            motions.push({ id: "m3", name: "Motion 3", distance: 100, accel: 1, decel: 1, maxSpeed: 500, waitTime: 0 });
        }
        upsertDraftNode(draft, {
            id: "m3",
            type: "motion",
            axisName: axis.name,
            motionId: "m3",
            motionName: "Motion 3",
            x: 1080,
            y: 30
        });
        if (endNode) endNode.x = 1360;
        upsertDraftConnection(draft, { from: secondMotion.id, to: "m3", output: "output_1", input: "input_1" });
        upsertDraftConnection(draft, { from: "m3", to: "end", output: "output_1", input: "input_1" });
        return;
    }

    axis.motions = motions.filter((motion) => motion.id !== "m3");
    if (endNode) endNode.x = 1080;
    upsertDraftConnection(draft, { from: secondMotion.id, to: "end", output: "output_1", input: "input_1" });
}

export function applyPlanAnswerToDraft(draft, questionId, rawValue, helpers = {}) {
    const extractErrorCodeFromText = helpers.extractErrorCodeFromText || defaultExtractErrorCodeFromText;
    const value = String(rawValue || "").trim();
    if (!draft || !questionId) return draft;

    if (questionId === "axis_name") {
        const axis = findDraftAxis(draft);
        if (axis) axis.name = value;
        for (const node of draft.nodes || []) {
            if (node.type === "motion") node.axisName = value;
        }
        draft.summary = `Draft updated for axis ${value}.`;
        return draft;
    }

    if (questionId === "timeout_sec") {
        const condition = findDraftConditionNode(draft);
        if (condition) condition.timeoutSec = Number(value);
        ensureTimeoutErrorBranch(draft, extractErrorCodeFromText(JSON.stringify(draft)) || "E001");
        return draft;
    }

    if (questionId === "error_code") {
        ensureTimeoutErrorBranch(draft, value);
        return draft;
    }

    if (questionId === "off_branch") {
        applyOffBranchToDraft(draft, value);
        return draft;
    }

    if (questionId === "after_motion") {
        applyAfterMotionPlanToDraft(draft, value);
        return draft;
    }

    return draft;
}

export function applyAnswersToDraft(draft, answers = {}, helpers = {}) {
    if (!draft || !answers || typeof answers !== "object") return draft;
    for (const [questionId, rawValue] of Object.entries(answers)) {
        applyPlanAnswerToDraft(draft, questionId, rawValue, helpers);
    }
    return draft;
}
